// Authenticated app shell: a full-width dark top bar + cream sidebar + routed content.
// Mirrors the consumer prototype's logged-in layout. Hosts the idle auto-logout.
import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Brand, ThemeToggle, Avatar, Sidebar, useNavCollapse } from "@championcare/ui";
import { useApi } from "../api";
import { useConversationsApi } from "../lib/conversationsApi";
import { useAppAuth } from "../auth";
import { useIdleLogout } from "../lib/useIdleLogout";

// Sidebar glyphs (stroke, currentColor), ported from the prototype.
const icons: Record<string, ReactNode> = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
  ),
  book: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
  ),
  visits: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
  ),
  messages: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5z" /></svg>
  ),
  insurance: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
  ),
  billing: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M2 9h20" /><path d="M6 15h4" /></svg>
  ),
  records: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
  ),
  profile: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 22v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" /></svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
  ),
  help: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
  ),
  signout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
  ),
  concierge: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8V4H8" /><rect x="4" y="8" width="16" height="12" rx="2" /><path d="M2 14h2" /><path d="M20 14h2" /><path d="M15 13v2" /><path d="M9 13v2" /></svg>
  ),
  utensils: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2h0a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" /></svg>
  ),
};

type NavItem = { to: string; label: string; icon: ReactNode; end?: boolean; external?: boolean };

const GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: "My care",
    items: [
      { to: "/", label: "Dashboard", icon: icons.dashboard, end: true },
      { to: "/visits/book", label: "Book a visit", icon: icons.book },
      { to: "/visits", label: "Visits", icon: icons.visits, end: true },
      { to: "/care", label: "Records", icon: icons.records },
      { to: "/messages", label: "Messages", icon: icons.messages },
      { to: "/concierge", label: "AI Concierge", icon: icons.concierge },
    ],
  },
  {
    label: "Wellness",
    items: [
      { to: "https://www.snapkitchen.com/", label: "Meal prep", icon: icons.utensils, external: true },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/account", label: "Profile", icon: icons.profile },
      { to: "/settings", label: "Settings", icon: icons.settings },
      { to: "/help", label: "Help", icon: icons.help },
    ],
  },
];

export function Shell() {
  const { displayName, signOut, isSignedIn } = useAppAuth();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const name = displayName ?? "Member";
  const initial = name.trim().charAt(0).toUpperCase() || "M";
  const [navOpen, setNavOpen] = useState(false);
  const { collapsed, setCollapsed, toggle } = useNavCollapse("cc-nav-collapsed-patient");
  // ≤1024px is the off-canvas drawer regime (Menu pill + full-label slide-over), never the in-flow
  // icon rail — so suppress the collapsed state there (the rail is desktop-only).
  const [isMobile, setIsMobile] = useState(() => {
    try {
      return window.matchMedia("(max-width: 1024px)").matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const navCollapsed = collapsed && !isMobile;
  // The member's own photo for the top-bar chip (avatar_url already arrives on /me); initials fallback.
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    api
      .me()
      .then((m) => active && setAvatarUrl(m?.avatar_url ?? null))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [api]);

  // Unread-message count for the Messages nav badge. Refetch on route change (opening a thread marks
  // it read → count drops) + poll every 5s for new inbound while navigating.
  const conversationsApi = useConversationsApi();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let active = true;
    const load = () => conversationsApi.unreadCount().then((c) => active && setUnread(c)).catch(() => {});
    load();
    const iv = setInterval(load, 5000);
    return () => {
      active = false;
      clearInterval(iv);
    };
  }, [conversationsApi, location.pathname]);

  useIdleLogout(() => {
    void signOut();
    navigate("/sign-in");
  }, isSignedIn);

  // Drive the off-canvas drawer + hamburger icon swap off `body.pt-nav-open` (mirrors marketing).
  useEffect(() => {
    document.body.classList.toggle("pt-nav-open", navOpen);
    return () => document.body.classList.remove("pt-nav-open");
  }, [navOpen]);

  const closeNav = () => setNavOpen(false);
  const logout = () => {
    closeNav();
    void signOut();
    navigate("/sign-in");
  };

  return (
    <div className="pt-root">
      <header className="pt-topbar">
        <Link to="/" className="pt-brand-link" aria-label="Champion Care — dashboard">
          <Brand onDark size={22} />
        </Link>
        <div className="pt-topbar__spacer" />
        <div className="pt-topbar__actions">
          <ThemeToggle />
          <span className="pt-userchip">
            <Avatar className="pt-userchip__avatar" src={avatarUrl} initials={initial} />
            <span className="pt-userchip__name">{name}</span>
          </span>
        </div>
      </header>

      <div className={`pt-shell ${navCollapsed ? "nav-collapsed" : ""}`}>
        <div className="pt-nav-backdrop" onClick={closeNav} aria-hidden="true" />
        <Sidebar collapsed={navCollapsed} onToggle={toggle} onExpand={() => setCollapsed(false)}>
          {GROUPS.map((group) => (
            <div key={group.label} style={{ display: "contents" }}>
              <div className="pt-navlabel">{group.label}</div>
              {group.items.map((n) =>
                n.external ? (
                  <a
                    key={n.to}
                    href={n.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeNav}
                    className="pt-navlink"
                    aria-label={`${n.label} (opens in a new tab)`}
                  >
                    {n.icon}
                    <span className="pt-navlink__label">{n.label}</span>
                  </a>
                ) : (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={closeNav}
                    className={({ isActive }) => `pt-navlink ${isActive ? "is-active" : ""}`}
                  >
                    {n.icon}
                    <span className="pt-navlink__label">{n.label}</span>
                    {n.to === "/messages" && unread > 0 && (
                      <span className="pt-navlink__badge">{unread > 99 ? "99+" : unread}</span>
                    )}
                  </NavLink>
                ),
              )}
            </div>
          ))}
          <button className="pt-navlink pt-navlink--signout" onClick={logout}>
            {icons.signout}
            <span className="pt-navlink__label">Sign out</span>
          </button>
        </Sidebar>

        <main className="pt-main">
          <button
            className="pt-menubtn"
            onClick={() => setNavOpen((o) => !o)}
            aria-label="Open menu"
            aria-expanded={navOpen}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
            Menu
          </button>
          <div key={location.pathname} className="page-fade">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
